import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  FiBold,
  FiItalic,
  FiLink,
  FiList,
  FiImage,
  FiCode,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiCopy,
  FiMaximize2,
  FiRefreshCw,
  FiUpload,
  FiX,
  FiAlertCircle,
  FiCrosshair,
} from 'react-icons/fi';
import { MdFormatListNumbered, MdTableChart, MdTitle } from 'react-icons/md';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import { useSeoMeta } from '../hooks/useSeoMeta.ts';
import { problemPath, slugify } from '../utils/problemUrls.ts';
import { authHref } from '../utils/authRedirect.ts';
import './css/SuggestProblemPage.css';

type Difficulty = 'Easy' | 'Medium' | 'Hard';
type DescTab = 'editor' | 'markdown';
type TemplateLang = 'python' | 'cpp' | 'java';
type TestVisibility = 'open' | 'hidden';

interface ExampleItem {
  id: string;
  input: string;
  output: string;
  explanation: string;
}

interface TestCaseItem {
  id: string;
  input: string;
  output: string;
  visibility: TestVisibility;
}

const SUGGEST_DRAFT_KEY = 'nn_suggest_problem_draft_v1';
const CREATE_DRAFT_KEY = 'nn_create_class_problem_draft_v1';
/** Timestamp (ms) until which the promo banner stays hidden after dismiss */
const PROMO_NOTICE_DISMISS_UNTIL_KEY = 'nn_suggest_problem_promo_dismiss_until';
/** Legacy flag from the previous forever-dismiss behavior */
const PROMO_NOTICE_DISMISS_LEGACY_KEY = 'nn_suggest_problem_promo_dismissed';
const PROMO_NOTICE_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const DESC_MAX = 10000;

function shouldShowPromoNotice(): boolean {
  try {
    const untilRaw = localStorage.getItem(PROMO_NOTICE_DISMISS_UNTIL_KEY);
    if (untilRaw) {
      const until = Number(untilRaw);
      if (Number.isFinite(until) && Date.now() < until) return false;
      return true;
    }
    // Migrate old permanent dismiss → start a cooldown from now
    if (localStorage.getItem(PROMO_NOTICE_DISMISS_LEGACY_KEY) === '1') {
      const until = Date.now() + PROMO_NOTICE_COOLDOWN_MS;
      localStorage.setItem(PROMO_NOTICE_DISMISS_UNTIL_KEY, String(until));
      localStorage.removeItem(PROMO_NOTICE_DISMISS_LEGACY_KEY);
      return false;
    }
    return true;
  } catch {
    return true;
  }
}

const DEFAULT_STARTERS: Record<TemplateLang, string> = {
  python: `n = int(input())
arr = list(map(int, input().split()))

# TODO: напишите решение здесь

print()`,
  cpp: `#include <bits/stdc++.h>
using namespace std;

int main() {
    ios::sync_with_stdio(false);
    cin.tie(nullptr);
    // TODO: напишите решение здесь
    return 0;
}`,
  java: `import java.util.*;

public class Main {
    public static void main(String[] args) {
        Scanner sc = new Scanner(System.in);
        // TODO: напишите решение здесь
    }
}`,
};

/** Method name (snake_case) from problem title slug, e.g. "Two Sum" → "two_sum" */
function methodSnakeFromTitle(title: string): string {
  const base = slugify(title).replace(/-/g, '_') || 'solve';
  return /^[0-9]/.test(base) ? `solve_${base}` : base;
}

function snakeToCamel(snake: string): string {
  return snake.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
}

const DEFAULT_SOLUTION_PARAMS = ['nums'] as const;

function buildSolutionStarters(title: string): Record<TemplateLang, string> {
  const snake = methodSnakeFromTitle(title);
  const camel = snakeToCamel(snake);
  return {
    python: `class Solution:
    def ${snake}(self, nums):
        # TODO: write your solution
        # Parameter names must match keys in test inputs
        return None
`,
    cpp: `#include <vector>
using namespace std;

class Solution {
public:
    // Parameter names must match keys in test inputs
    int ${camel}(vector<int>& nums) {
        // TODO: write your solution
        return 0;
    }
};
`,
    java: `class Solution {
    // Parameter names must match keys in test inputs
    public int ${camel}(int[] nums) {
        // TODO: write your solution
        return 0;
    }
}
`,
  };
}

/** Extract param names from Python Solution starter: def foo(self, a, b) */
function extractPythonSolutionParams(code: string): string[] {
  const match = code.match(/def\s+\w+\s*\(\s*self\s*(?:,\s*([^)]*))?\)/);
  if (!match) return [...DEFAULT_SOLUTION_PARAMS];
  const argsPart = (match[1] || '').trim();
  if (!argsPart) return [];
  return argsPart
    .split(',')
    .map((part) => part.split(':')[0].trim().split('=')[0].trim())
    .filter((name) => name && !name.startsWith('*'));
}

/**
 * Normalize a create-mode test input into structured form for the Solution runner.
 * - {"nums":[1,2]} → object
 * - nums = [1, 2] → keep string (line format)
 * - bare [1,2] with one method param → wrap as {param: value}
 */
function normalizeCreateTestInput(
  raw: string,
  paramNames: string[],
): { ok: true; value: unknown } | { ok: false; reason: 'bare_value' | 'empty' } {
  const text = raw.trim();
  if (!text) return { ok: false, reason: 'empty' };

  if (text.includes('=') && !text.trimStart().startsWith('{') && !text.trimStart().startsWith('[')) {
    return { ok: true, value: text };
  }

  try {
    const parsed = JSON.parse(text);
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return { ok: true, value: parsed };
    }
    if (paramNames.length === 1) {
      return { ok: true, value: { [paramNames[0]]: parsed } };
    }
    return { ok: false, reason: 'bare_value' };
  } catch {
    return { ok: true, value: text };
  }
}

function normalizeCreateTestOutput(raw: string): unknown {
  const text = raw.trim();
  if (!text) return '';
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function uid(): string {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function emptyExample(): ExampleItem {
  return { id: uid(), input: '', output: '', explanation: '' };
}

function emptyTest(visibility: TestVisibility = 'hidden'): TestCaseItem {
  return { id: uid(), input: '', output: '', visibility };
}

type ProgressStepId = 'condition' | 'checks' | 'solution' | 'publish';

export default function SuggestProblemPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { t, currentLanguage } = useTranslation();

  const isCreateMode = location.pathname === '/problems/create';
  const classId = Number(searchParams.get('classId'));
  const problemId = Number(searchParams.get('problemId'));
  const isEditMode = isCreateMode && Number.isInteger(problemId) && problemId > 0;
  const createTargetsValid = Number.isInteger(classId) && classId > 0;
  const draftStorageKey = isCreateMode ? CREATE_DRAFT_KEY : SUGGEST_DRAFT_KEY;
  const pagePath = isCreateMode
    ? isEditMode
      ? `/problems/create?classId=${classId}&problemId=${problemId}`
      : `/problems/create?classId=${classId}`
    : '/problems/suggest';
  const classReturnPath = createTargetsValid
    ? `/class/${classId}?tab=customProblems`
    : '/my-class?tab=customProblems';

  useSeoMeta({
    title: isEditMode
      ? t('createProblem.editTitle')
      : isCreateMode
        ? t('createProblem.title')
        : t('suggestProblem.title'),
    description:
      currentLanguage === 'ru'
        ? isEditMode
          ? 'Отредактируйте задачу своего класса на Sciplex.'
          : isCreateMode
            ? 'Создайте задачу для своего класса на Sciplex.'
            : 'Создайте задание и настройте проверку решений на Sciplex.'
        : isEditMode
          ? 'Edit a custom problem for your class on Sciplex.'
          : isCreateMode
            ? 'Create a custom problem for your class on Sciplex.'
            : 'Create a practice problem and configure solution checking on Sciplex.',
    canonicalPath: isCreateMode ? '/problems/create' : '/problems/suggest',
    noIndex: true,
  });

  const [topics, setTopics] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [difficulty, setDifficulty] = useState<Difficulty>('Medium');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [languages, setLanguages] = useState<TemplateLang[]>(['python', 'cpp', 'java']);

  const [descTab, setDescTab] = useState<DescTab>('editor');
  const [description, setDescription] = useState('');
  const [inputFormat, setInputFormat] = useState('');
  const [outputFormat, setOutputFormat] = useState('');
  const [constraintsText, setConstraintsText] = useState('');
  const [notes, setNotes] = useState('');

  const [examples, setExamples] = useState<ExampleItem[]>([emptyExample(), emptyExample()]);
  const [tests, setTests] = useState<TestCaseItem[]>([
    emptyTest('open'),
    emptyTest('open'),
    emptyTest('hidden'),
    emptyTest('hidden'),
    emptyTest('hidden'),
  ]);
  const [editingTestId, setEditingTestId] = useState<string | null>(null);

  const [templateLang, setTemplateLang] = useState<TemplateLang>('python');
  const [starters, setStarters] = useState<Record<TemplateLang, string>>({ ...DEFAULT_STARTERS });
  const [templateFullscreen, setTemplateFullscreen] = useState(false);
  const [solutionTemplateLang, setSolutionTemplateLang] = useState<TemplateLang>('python');
  const [solutionStarters, setSolutionStarters] = useState<Record<TemplateLang, string>>(() =>
    buildSolutionStarters(''),
  );
  const [solutionStartersTouched, setSolutionStartersTouched] = useState(false);
  const [solutionFullscreen, setSolutionFullscreen] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [createdProblemPath, setCreatedProblemPath] = useState<string | null>(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [showPromoNotice, setShowPromoNotice] = useState(() => !isCreateMode && shouldShowPromoNotice());
  const [problemLoading, setProblemLoading] = useState(isEditMode);

  const descRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user) {
      navigate(authHref('/login', pagePath), { state: { from: pagePath } });
    }
  }, [authLoading, isAuthenticated, user, navigate, pagePath]);

  useEffect(() => {
    if (!isCreateMode) return;
    if (!createTargetsValid) {
      navigate('/my-class', { replace: true });
    }
  }, [isCreateMode, createTargetsValid, navigate]);

  const DEFAULT_TOPICS = ['Algorithms', 'SQL', 'Math'];

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/problems/topics_and_tags`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (Array.isArray(data.topics) && data.topics.length > 0) {
          setTopics(data.topics);
        } else {
          setTopics(DEFAULT_TOPICS);
        }
        if (Array.isArray(data.tags)) {
          setAvailableTags(data.tags.filter((x: unknown): x is string => typeof x === 'string'));
        }
      } catch {
        setTopics(DEFAULT_TOPICS);
      }
    };
    fetchMeta();
  }, []);

  useEffect(() => {
    if (isEditMode) return;
    try {
      const raw = localStorage.getItem(draftStorageKey);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (typeof d.title === 'string') setTitle(d.title);
      if (typeof d.topic === 'string') setTopic(d.topic);
      if (d.difficulty === 'Easy' || d.difficulty === 'Medium' || d.difficulty === 'Hard') {
        setDifficulty(d.difficulty);
      }
      if (Array.isArray(d.tags)) setTags(d.tags.filter((x: unknown): x is string => typeof x === 'string'));
      if (Array.isArray(d.languages)) {
        const langs = d.languages.filter((x: string) => x === 'python' || x === 'cpp' || x === 'java');
        if (langs.length) setLanguages(langs);
      }
      if (typeof d.description === 'string') setDescription(d.description);
      if (typeof d.inputFormat === 'string') setInputFormat(d.inputFormat);
      if (typeof d.outputFormat === 'string') setOutputFormat(d.outputFormat);
      if (typeof d.constraintsText === 'string') setConstraintsText(d.constraintsText);
      if (typeof d.notes === 'string') setNotes(d.notes);
      if (Array.isArray(d.examples) && d.examples.length) {
        setExamples(
          d.examples.map((ex: Partial<ExampleItem>) => ({
            id: typeof ex.id === 'string' ? ex.id : uid(),
            input: String(ex.input || ''),
            output: String(ex.output || ''),
            explanation: String(ex.explanation || ''),
          })),
        );
      }
      if (Array.isArray(d.tests) && d.tests.length) {
        setTests(
          d.tests.map((tc: Partial<TestCaseItem>) => ({
            id: typeof tc.id === 'string' ? tc.id : uid(),
            input: String(tc.input || ''),
            output: String(tc.output || ''),
            visibility: tc.visibility === 'open' ? 'open' : 'hidden',
          })),
        );
      }
      if (d.starters && typeof d.starters === 'object') {
        setStarters({
          python: String(d.starters.python ?? DEFAULT_STARTERS.python),
          cpp: String(d.starters.cpp ?? DEFAULT_STARTERS.cpp),
          java: String(d.starters.java ?? DEFAULT_STARTERS.java),
        });
      }
      if (d.solutionStarters && typeof d.solutionStarters === 'object') {
        const generated = buildSolutionStarters(typeof d.title === 'string' ? d.title : '');
        setSolutionStarters({
          python: String(d.solutionStarters.python ?? generated.python),
          cpp: String(d.solutionStarters.cpp ?? generated.cpp),
          java: String(d.solutionStarters.java ?? generated.java),
        });
        setSolutionStartersTouched(!!d.solutionStartersTouched);
      }
    } catch {
      /* ignore corrupt draft */
    }
  }, [draftStorageKey, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !createTargetsValid) return;
    let cancelled = false;
    const loadProblem = async () => {
      setProblemLoading(true);
      setError(null);
      try {
        const r = await fetch(
          `${API_BASE}/api/classes/${classId}/custom-problems/${problemId}`,
          { credentials: 'include' },
        );
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (r.status === 401) {
          navigate(authHref('/login', pagePath), { state: { from: pagePath } });
          return;
        }
        if (!r.ok || !data.problem) {
          setError(data?.message || t('createProblem.editLoadError'));
          window.setTimeout(() => navigate(classReturnPath, { replace: true }), 1200);
          return;
        }
        const p = data.problem;
        setTitle(typeof p.title === 'string' ? p.title : '');
        setTopic(typeof p.topic === 'string' ? p.topic : '');
        if (p.difficulty === 'Easy' || p.difficulty === 'Medium' || p.difficulty === 'Hard') {
          setDifficulty(p.difficulty);
        }
        setDescription(typeof p.description === 'string' ? p.description : '');
        setInputFormat(typeof p.input_format === 'string' ? p.input_format : '');
        setOutputFormat(typeof p.output_format === 'string' ? p.output_format : '');
        setNotes(typeof p.notes === 'string' ? p.notes : '');
        setConstraintsText(Array.isArray(p.constraints) ? p.constraints.join('\n') : '');
        setTags(Array.isArray(p.tags) ? p.tags.filter((x: unknown): x is string => typeof x === 'string') : []);
        if (Array.isArray(p.languages)) {
          const langs = p.languages.filter((x: string) => x === 'python' || x === 'cpp' || x === 'java');
          if (langs.length) setLanguages(langs);
        }
        setExamples(
          Array.isArray(p.examples) && p.examples.length
            ? p.examples.map((ex: Partial<ExampleItem>) => ({
                id: uid(),
                input: String(ex.input || ''),
                output: String(ex.output || ''),
                explanation: String(ex.explanation || ''),
              }))
            : [emptyExample(), emptyExample()],
        );
        setTests(
          Array.isArray(p.test_cases) && p.test_cases.length
            ? p.test_cases.map((tc: { input?: unknown; output?: unknown }) => ({
                id: uid(),
                input: typeof tc.input === 'string' ? tc.input : JSON.stringify(tc.input ?? ''),
                output: typeof tc.output === 'string' ? tc.output : JSON.stringify(tc.output ?? ''),
                visibility: 'hidden' as TestVisibility,
              }))
            : [emptyTest('open'), emptyTest('open'), emptyTest('hidden')],
        );
        const starter = p.starter_code && typeof p.starter_code === 'object' ? p.starter_code : {};
        const generated = buildSolutionStarters(typeof p.title === 'string' ? p.title : '');
        setSolutionStarters({
          python: String(starter.python ?? generated.python),
          cpp: String(starter.cpp ?? generated.cpp),
          java: String(starter.java ?? generated.java),
        });
        setSolutionStartersTouched(true);
      } catch {
        if (!cancelled) {
          setError(t('createProblem.editLoadError'));
        }
      } finally {
        if (!cancelled) setProblemLoading(false);
      }
    };
    loadProblem();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, createTargetsValid, classId, problemId, navigate, pagePath, classReturnPath, t]);

  useEffect(() => {
    if (!isCreateMode || isEditMode || solutionStartersTouched) return;
    setSolutionStarters(buildSolutionStarters(title));
  }, [isCreateMode, isEditMode, title, solutionStartersTouched]);

  const progressSteps = useMemo(() => {
    const hasExample = examples.some(
      (ex) => ex.input.trim() && ex.output.trim(),
    );
    const hasTests = tests.some((tc) => tc.input.trim() && tc.output.trim());
    const starterTouched = (Object.keys(DEFAULT_STARTERS) as TemplateLang[]).some(
      (lang) => starters[lang] !== DEFAULT_STARTERS[lang],
    );

    const conditionDone = Boolean(title.trim() && topic.trim() && description.trim());
    const checksDone = hasTests || hasExample;
    const solutionTouchedCreate =
      isCreateMode &&
      (solutionStartersTouched ||
        extractPythonSolutionParams(solutionStarters.python).length > 0);
    const solutionDone = isCreateMode
      ? languages.length > 0 && (solutionTouchedCreate || hasExample || hasTests)
      : languages.length > 0 && (starterTouched || hasExample);
    const publishDone = conditionDone && checksDone && solutionDone;

    const steps: Array<{ id: ProgressStepId; labelKey: string; done: boolean }> = [
      { id: 'condition', labelKey: 'suggestProblem.stepCondition', done: conditionDone },
      { id: 'checks', labelKey: 'suggestProblem.stepChecks', done: checksDone },
      { id: 'solution', labelKey: 'suggestProblem.stepSolution', done: solutionDone },
      { id: 'publish', labelKey: 'suggestProblem.stepPublish', done: publishDone },
    ];

    const activeIndex = steps.findIndex((s) => !s.done);
    return steps.map((step, index) => ({
      ...step,
      state: step.done ? 'done' : activeIndex === index ? 'active' : 'todo',
    }));
  }, [title, topic, description, examples, tests, languages, starters, isCreateMode, solutionStarters]);

  const solutionParamNames = useMemo(
    () => extractPythonSolutionParams(solutionStarters.python),
    [solutionStarters.python],
  );

  const createTestInputPlaceholder = useMemo(() => {
    if (solutionParamNames.length === 0) return '{"nums": [1, 2]}';
    if (solutionParamNames.length === 1) {
      return `{"${solutionParamNames[0]}": [1, 2]}`;
    }
    const entries = solutionParamNames.map((name, i) =>
      i === 0 ? `"${name}": [1, 2]` : `"${name}": 3`,
    );
    return `{${entries.join(', ')}}`;
  }, [solutionParamNames]);

  const saveDraft = () => {
    const payload = {
      title,
      topic,
      difficulty,
      tags,
      languages,
      description,
      inputFormat,
      outputFormat,
      constraintsText,
      notes,
      examples,
      tests,
      starters,
      solutionStarters,
      solutionStartersTouched,
    };
    localStorage.setItem(draftStorageKey, JSON.stringify(payload));
    setDraftSaved(true);
    window.setTimeout(() => setDraftSaved(false), 2500);
  };

  const insertMarkdown = (before: string, after = before) => {
    const el = descRef.current;
    if (!el) {
      setDescription((prev) => `${prev}${before}${after}`);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = description.slice(start, end);
    const next = `${description.slice(0, start)}${before}${selected || '…'}${after}${description.slice(end)}`;
    setDescription(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + before.length + (selected || '…').length;
      el.setSelectionRange(pos, pos);
    });
  };

  const addTag = (raw: string) => {
    const value = raw.trim();
    if (!value || tags.includes(value) || tags.length >= 12) return;
    setTags((prev) => [...prev, value]);
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setTags((prev) => prev.filter((x) => x !== tag));
  };

  const toggleLanguage = (lang: TemplateLang) => {
    setLanguages((prev) => {
      if (prev.includes(lang)) {
        if (prev.length === 1) return prev;
        const next = prev.filter((l) => l !== lang);
        if (!next.includes(templateLang)) setTemplateLang(next[0]);
        return next;
      }
      return [...prev, lang];
    });
  };

  const resetStarter = () => {
    setStarters((prev) => ({ ...prev, [templateLang]: DEFAULT_STARTERS[templateLang] }));
  };

  const resetSolutionStarter = () => {
    setSolutionStarters(buildSolutionStarters(title));
    setSolutionStartersTouched(false);
  };

  const copyStarter = async () => {
    try {
      await navigator.clipboard.writeText(starters[templateLang]);
    } catch {
      /* ignore */
    }
  };

  const copySolutionStarter = async () => {
    try {
      await navigator.clipboard.writeText(solutionStarters[solutionTemplateLang]);
    } catch {
      /* ignore */
    }
  };

  const addExample = () => {
    if (examples.length >= 10) return;
    setExamples((prev) => [...prev, emptyExample()]);
  };

  const updateExample = (id: string, patch: Partial<ExampleItem>) => {
    setExamples((prev) => prev.map((ex) => (ex.id === id ? { ...ex, ...patch } : ex)));
  };

  const removeExample = (id: string) => {
    setExamples((prev) => (prev.length <= 1 ? prev : prev.filter((ex) => ex.id !== id)));
  };

  const addTest = () => {
    const tc = emptyTest('hidden');
    setTests((prev) => [...prev, tc]);
    setEditingTestId(tc.id);
  };

  const updateTest = (id: string, patch: Partial<TestCaseItem>) => {
    setTests((prev) => prev.map((tc) => (tc.id === id ? { ...tc, ...patch } : tc)));
  };

  const removeTest = (id: string) => {
    setTests((prev) => prev.filter((tc) => tc.id !== id));
    if (editingTestId === id) setEditingTestId(null);
  };

  const importTests = () => {
    const raw = window.prompt(t('suggestProblem.importPrompt'));
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const list = Array.isArray(parsed) ? parsed : parsed?.tests;
      if (!Array.isArray(list)) throw new Error('bad');
      const next: TestCaseItem[] = list.slice(0, 50).map((item: any) => ({
        id: uid(),
        input: String(item.input ?? item.in ?? ''),
        output: String(item.output ?? item.out ?? item.expected ?? ''),
        visibility: item.visibility === 'open' || item.public === true ? 'open' : 'hidden',
      }));
      if (!next.length) throw new Error('empty');
      setTests(next);
    } catch {
      setError(t('suggestProblem.importError'));
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setCreatedProblemPath(null);
    const tTrim = title.trim();
    const topTrim = topic.trim();
    if (!tTrim) {
      setError(t('suggestProblem.errorTitle'));
      return;
    }
    if (!topTrim) {
      setError(t('suggestProblem.errorTopic'));
      return;
    }
    if (!description.trim()) {
      setError(t('suggestProblem.errorDescription'));
      return;
    }
    if (isCreateMode && !createTargetsValid) {
      setError(t('createProblem.errorContext'));
      return;
    }

    setSubmitting(true);
    try {
      const constraints = constraintsText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);

      const examplesPayload = examples
        .filter((ex) => ex.input.trim() || ex.output.trim() || ex.explanation.trim())
        .map((ex) => ({
          input: ex.input.trim(),
          output: ex.output.trim(),
          explanation: ex.explanation.trim(),
        }));

      const solutionParams = isCreateMode
        ? extractPythonSolutionParams(solutionStarters.python)
        : [];

      let testsPayload: Array<{ input: unknown; output: unknown }>;
      if (isCreateMode) {
        const normalized: Array<{ input: unknown; output: unknown }> = [];
        for (const tc of tests) {
          if (!tc.input.trim() && !tc.output.trim()) continue;
          const inputResult = normalizeCreateTestInput(tc.input, solutionParams);
            if (!inputResult.ok) {
              setError(
                inputResult.reason === 'empty'
                  ? t('createProblem.errorTestInputEmpty')
                  : t('createProblem.errorTestInputFormat'),
              );
              return;
            }
          normalized.push({
            input: inputResult.value,
            output: normalizeCreateTestOutput(tc.output),
          });
        }
        testsPayload = normalized;
      } else {
        testsPayload = tests
          .filter((tc) => tc.input.trim() || tc.output.trim())
          .map((tc) => ({
            input: tc.input.trim(),
            output: tc.output.trim(),
          }));
      }

      const payload = {
        title: tTrim,
        difficulty,
        topic: topTrim,
        description: description.trim(),
        input_format: inputFormat.trim(),
        output_format: outputFormat.trim(),
        notes: notes.trim(),
        constraints,
        examples: examplesPayload,
        tags,
        languages,
        test_cases: testsPayload,
        starter_code: isCreateMode ? solutionStarters : starters,
        is_premium: false,
      };

      const endpoint = isEditMode
        ? `${API_BASE}/api/classes/${classId}/custom-problems/${problemId}`
        : isCreateMode
          ? `${API_BASE}/api/classes/${classId}/problems/create`
          : `${API_BASE}/api/problem-suggestions`;

      const r = await fetch(endpoint, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await r.json().catch(() => ({}));
      if (r.status === 401) {
        navigate(authHref('/login', pagePath), { state: { from: pagePath } });
        return;
      }
      if (!r.ok) {
        setError(
          data?.message ||
            (isCreateMode
              ? isEditMode
                ? t('createProblem.editErrorSend')
                : t('createProblem.errorSend')
              : t('suggestProblem.errorSend')),
        );
        return;
      }

      if (!isEditMode) {
        localStorage.removeItem(draftStorageKey);
      }
      setSuccess(true);

      if (isCreateMode) {
        const pathFromApi = typeof data.path === 'string' ? data.path : null;
        const pathFromProblem =
          data.problem && typeof data.problem === 'object'
            ? problemPath(data.problem)
            : null;
        const nextPath = pathFromApi || pathFromProblem;
        setCreatedProblemPath(nextPath);
        window.setTimeout(() => {
          navigate(classReturnPath);
        }, 1200);
      } else {
        window.setTimeout(() => setSuccess(false), 5000);
      }
    } catch {
      setError(
        isCreateMode
          ? isEditMode
            ? t('createProblem.editErrorSend')
            : t('createProblem.errorSend')
          : t('suggestProblem.errorSend'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const dismissPromoNotice = () => {
    setShowPromoNotice(false);
    try {
      localStorage.setItem(
        PROMO_NOTICE_DISMISS_UNTIL_KEY,
        String(Date.now() + PROMO_NOTICE_COOLDOWN_MS),
      );
      localStorage.removeItem(PROMO_NOTICE_DISMISS_LEGACY_KEY);
    } catch {
      /* ignore */
    }
  };

  if (authLoading || !user || (isEditMode && problemLoading)) {
    return <div className="create-problem-loading">{t('suggestProblem.loading')}</div>;
  }

  const difficultyClass =
    difficulty === 'Easy' ? 'is-easy' : difficulty === 'Medium' ? 'is-medium' : 'is-hard';

  return (
    <div className="create-problem-page">
      <div className="create-problem-container">
        {showPromoNotice && !isCreateMode && (
          <div className="create-problem-promo" role="status">
            <p>
              {t('suggestProblem.promoNotice')}{' '}
              <Link to="/promo/development" className="create-problem-promo-link">
                {t('suggestProblem.promoNoticeMore')}
              </Link>
            </p>
            <button
              type="button"
              className="create-problem-promo-close"
              onClick={dismissPromoNotice}
              aria-label={t('suggestProblem.promoNoticeClose')}
            >
              <FiX size={16} />
            </button>
          </div>
        )}

        <header className="create-problem-header">
          <div className="create-problem-header-top">
            <div className="create-problem-header-text">
              <h1>
                {isEditMode
                  ? t('createProblem.editTitle')
                  : isCreateMode
                    ? t('createProblem.title')
                    : t('suggestProblem.title')}
              </h1>
              {isCreateMode && (
                <p className="create-problem-header-sub">
                  {isEditMode ? t('createProblem.editIntro') : t('createProblem.intro')}
                </p>
              )}
            </div>
            <div className="create-problem-header-actions">
              {isCreateMode && (
                <button
                  type="button"
                  className="cp-btn cp-btn--ghost"
                  onClick={() => navigate(classReturnPath)}
                >
                  {t('createProblem.backToClass')}
                </button>
              )}
              {!isEditMode && (
                <button type="button" className="cp-btn cp-btn--ghost" onClick={saveDraft}>
                  {draftSaved ? t('suggestProblem.draftSaved') : t('suggestProblem.saveDraft')}
                </button>
              )}
              <button
                type="button"
                className="cp-btn cp-btn--primary"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting
                  ? isEditMode
                    ? t('createProblem.editSending')
                    : isCreateMode
                      ? t('createProblem.sending')
                      : t('suggestProblem.sending')
                  : isEditMode
                    ? t('createProblem.editSubmit')
                    : isCreateMode
                      ? t('createProblem.submit')
                      : t('suggestProblem.submit')}
              </button>
            </div>
          </div>

          <nav className="cp-progress" aria-label={t('suggestProblem.progressLabel')}>
            <span className="cp-progress-start" aria-hidden>
              <FiCrosshair size={16} />
            </span>
            {progressSteps.map((step, index) => (
              <React.Fragment key={step.id}>
                {index > 0 && (
                  <span
                    className={`cp-progress-line ${
                      progressSteps[index - 1].state === 'done' ? 'is-done' : ''
                    }`}
                    aria-hidden
                  />
                )}
                <div className={`cp-progress-step is-${step.state}`}>
                  <span className="cp-progress-dot" aria-hidden />
                  <span className="cp-progress-label">{t(step.labelKey)}</span>
                </div>
              </React.Fragment>
            ))}
          </nav>
        </header>

        {error && (
          <div className="create-problem-alert create-problem-alert--error" role="alert">
            {error}
            <button type="button" className="create-problem-alert-close" onClick={() => setError(null)} aria-label="Close">
              <FiX size={14} />
            </button>
          </div>
        )}
        {success && (
          <div className="create-problem-alert create-problem-alert--success" role="status">
            {isCreateMode ? (
              <>
                {isEditMode ? t('createProblem.editSent') : t('createProblem.sent')}
                {createdProblemPath && (
                  <>
                    {' '}
                    <Link to={createdProblemPath}>{t('createProblem.openProblem')}</Link>
                  </>
                )}
              </>
            ) : (
              t('suggestProblem.sent')
            )}
          </div>
        )}

        <div className="create-problem-grid">
          <div className="create-problem-main">
            {/* 1. Basic info */}
            <section className="cp-card">
              <div className="cp-card-head">
                <h2>{t('suggestProblem.sectionBasic')}</h2>
              </div>
              <div className="cp-card-body">
                <div className="cp-field-grid cp-field-grid--title-row">
                  <div className="cp-field cp-field--title">
                    <label htmlFor="cp-title">
                      {t('suggestProblem.fieldTitle')} <span className="cp-req">*</span>
                    </label>
                    <input
                      id="cp-title"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={t('suggestProblem.placeholderTitle')}
                      maxLength={512}
                    />
                  </div>
                  <div className="cp-field">
                    <label htmlFor="cp-topic">
                      {t('suggestProblem.fieldTopic')} <span className="cp-req">*</span>
                    </label>
                    <select id="cp-topic" value={topic} onChange={(e) => setTopic(e.target.value)}>
                      <option value="">{t('suggestProblem.placeholderTopic')}</option>
                      {topics.map((tp) => (
                        <option key={tp} value={tp}>
                          {tp}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="cp-field">
                    <label htmlFor="cp-difficulty">{t('suggestProblem.fieldDifficulty')}</label>
                    <div className={`cp-select-wrap ${difficultyClass}`}>
                      <span className="cp-dot" aria-hidden />
                      <select
                        id="cp-difficulty"
                        value={difficulty}
                        onChange={(e) => setDifficulty(e.target.value as Difficulty)}
                      >
                        <option value="Easy">{t('suggestProblem.difficultyEasy')}</option>
                        <option value="Medium">{t('suggestProblem.difficultyMedium')}</option>
                        <option value="Hard">{t('suggestProblem.difficultyHard')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="cp-field-grid cp-field-grid--tags-langs">
                  <div className="cp-field">
                    <label>{t('suggestProblem.fieldTags')}</label>
                    <div className="cp-tags">
                      {tags.map((tag) => (
                        <span key={tag} className="cp-tag">
                          {tag}
                          <button type="button" onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>
                            <FiX size={12} />
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        className="cp-tag-input"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            addTag(tagInput.replace(/,/g, ''));
                          }
                        }}
                        placeholder={t('suggestProblem.placeholderTags')}
                        list="cp-tags-datalist"
                      />
                      <datalist id="cp-tags-datalist">
                        {availableTags.map((tg) => (
                          <option key={tg} value={tg} />
                        ))}
                      </datalist>
                      <button
                        type="button"
                        className="cp-tag-add"
                        onClick={() => addTag(tagInput)}
                        aria-label={t('suggestProblem.addTag')}
                      >
                        <FiPlus size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="cp-field">
                    <label>{t('suggestProblem.fieldLanguages')}</label>
                    <div className="cp-lang-chips" role="group" aria-label={t('suggestProblem.fieldLanguages')}>
                      {(
                        [
                          ['python', 'Python 3'],
                          ['cpp', 'C++'],
                          ['java', 'Java'],
                        ] as const
                      ).map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          className={`cp-lang-chip ${languages.includes(key) ? 'is-active' : ''}`}
                          onClick={() => toggleLanguage(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 2. Statement */}
            <section className="cp-card">
              <div className="cp-card-head">
                <h2>{t('suggestProblem.sectionStatement')}</h2>
                <div className="cp-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={descTab === 'editor'}
                    className={descTab === 'editor' ? 'is-active' : ''}
                    onClick={() => setDescTab('editor')}
                  >
                    {t('suggestProblem.tabEditor')}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={descTab === 'markdown'}
                    className={descTab === 'markdown' ? 'is-active' : ''}
                    onClick={() => setDescTab('markdown')}
                  >
                    Markdown
                  </button>
                </div>
              </div>
              <div className="cp-card-body">
                {descTab === 'editor' && (
                  <div className="cp-toolbar" role="toolbar" aria-label="Formatting">
                    <button type="button" onClick={() => insertMarkdown('**')} title="Bold">
                      <FiBold size={15} />
                    </button>
                    <button type="button" onClick={() => insertMarkdown('*')} title="Italic">
                      <FiItalic size={15} />
                    </button>
                    <button type="button" onClick={() => insertMarkdown('## ', '')} title="Heading">
                      <MdTitle size={16} />
                    </button>
                    <button type="button" onClick={() => insertMarkdown('- ', '')} title="List">
                      <FiList size={15} />
                    </button>
                    <button type="button" onClick={() => insertMarkdown('1. ', '')} title="Numbered list">
                      <MdFormatListNumbered size={16} />
                    </button>
                    <button type="button" onClick={() => insertMarkdown('`')} title="Code">
                      <FiCode size={15} />
                    </button>
                    <button type="button" onClick={() => insertMarkdown('[', '](url)')} title="Link">
                      <FiLink size={15} />
                    </button>
                    <button type="button" onClick={() => insertMarkdown('![alt](', ')')} title="Image">
                      <FiImage size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => insertMarkdown('|  |  |\n|---|---|\n|  |  |', '')}
                      title="Table"
                    >
                      <MdTableChart size={16} />
                    </button>
                  </div>
                )}
                <div className="cp-desc-wrap">
                  <textarea
                    ref={descRef}
                    className="cp-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, DESC_MAX))}
                    placeholder={t('suggestProblem.placeholderDescription')}
                    rows={descTab === 'markdown' ? 12 : 8}
                  />
                  <span className="cp-char-count">
                    {description.length} / {DESC_MAX}
                  </span>
                </div>

                <div className="cp-meta-grid">
                  <div className="cp-meta-box">
                    <label htmlFor="cp-input-format">{t('suggestProblem.inputFormat')}</label>
                    <textarea
                      id="cp-input-format"
                      value={inputFormat}
                      onChange={(e) => setInputFormat(e.target.value)}
                      placeholder={t('suggestProblem.placeholderInputFormat')}
                      rows={3}
                    />
                  </div>
                  <div className="cp-meta-box">
                    <label htmlFor="cp-output-format">{t('suggestProblem.outputFormat')}</label>
                    <textarea
                      id="cp-output-format"
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value)}
                      placeholder={t('suggestProblem.placeholderOutputFormat')}
                      rows={3}
                    />
                  </div>
                  <div className="cp-meta-box">
                    <label htmlFor="cp-constraints">{t('suggestProblem.fieldConstraints')}</label>
                    <textarea
                      id="cp-constraints"
                      value={constraintsText}
                      onChange={(e) => setConstraintsText(e.target.value)}
                      placeholder={t('suggestProblem.placeholderConstraints')}
                      rows={3}
                    />
                  </div>
                  <div className="cp-meta-box">
                    <label htmlFor="cp-notes">{t('suggestProblem.notes')}</label>
                    <textarea
                      id="cp-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={t('suggestProblem.placeholderNotes')}
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* 3. Examples */}
            <section className="cp-card">
              <div className="cp-card-head">
                <h2>{t('suggestProblem.exampleSection')}</h2>
                <button type="button" className="cp-btn cp-btn--sm cp-btn--ghost" onClick={addExample}>
                  <FiPlus size={14} />
                  {t('suggestProblem.addExample')}
                </button>
              </div>
              <div className="cp-card-body">
                <div className="cp-examples-grid">
                  {examples.map((ex, idx) => (
                    <div key={ex.id} className="cp-example-card">
                      <div className="cp-example-card-head">
                        <span>
                          {t('suggestProblem.exampleLabel')} {idx + 1}
                        </span>
                        {examples.length > 1 && (
                          <button type="button" className="cp-icon-btn" onClick={() => removeExample(ex.id)} aria-label="Remove">
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                      <div className="cp-field">
                        <label>{t('suggestProblem.exampleInput')}</label>
                        <textarea
                          value={ex.input}
                          onChange={(e) => updateExample(ex.id, { input: e.target.value })}
                          rows={3}
                          placeholder={t('suggestProblem.placeholderExampleInput')}
                        />
                      </div>
                      <div className="cp-field">
                        <label>{t('suggestProblem.exampleOutput')}</label>
                        <input
                          type="text"
                          value={ex.output}
                          onChange={(e) => updateExample(ex.id, { output: e.target.value })}
                          placeholder={t('suggestProblem.placeholderExampleOutput')}
                        />
                      </div>
                      <div className="cp-field">
                        <label>{t('suggestProblem.exampleExplanation')}</label>
                        <textarea
                          value={ex.explanation}
                          onChange={(e) => updateExample(ex.id, { explanation: e.target.value })}
                          rows={2}
                          placeholder={t('suggestProblem.placeholderExampleExplanation')}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <aside className="create-problem-side">
            {/* Create mode: Solution first, then tests. Suggest mode: tests, then stdin template. */}
            {isCreateMode && (
              <section className={`cp-card ${solutionFullscreen ? 'is-fullscreen' : ''}`}>
                <div className="cp-card-head">
                  <h2>{t('createProblem.sectionSolutionStarter')}</h2>
                  <button type="button" className="cp-btn cp-btn--sm cp-btn--ghost" onClick={resetSolutionStarter}>
                    <FiRefreshCw size={13} />
                    {t('suggestProblem.resetTemplate')}
                  </button>
                </div>
                <div className="cp-card-body">
                  <p className="cp-hint-inline">{t('createProblem.solutionStarterHint')}</p>
                  {solutionParamNames.length > 0 && (
                    <p className="cp-hint-inline cp-hint-inline--params">
                      {t('createProblem.solutionParamsLabel')}{' '}
                      <code>{solutionParamNames.join(', ')}</code>
                    </p>
                  )}
                  <div className="cp-lang-tabs">
                    {(
                      [
                        ['python', 'Python 3'],
                        ['cpp', 'C++17'],
                        ['java', 'Java 17'],
                      ] as const
                    )
                      .filter(([key]) => languages.includes(key))
                      .map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          className={solutionTemplateLang === key ? 'is-active' : ''}
                          onClick={() => setSolutionTemplateLang(key)}
                        >
                          {label}
                        </button>
                      ))}
                  </div>
                  <div className="cp-code-wrap">
                    <div className="cp-code-tools">
                      <button
                        type="button"
                        className="cp-icon-btn"
                        onClick={copySolutionStarter}
                        title="Copy"
                        aria-label="Copy"
                      >
                        <FiCopy size={14} />
                      </button>
                      <button
                        type="button"
                        className="cp-icon-btn"
                        onClick={() => setSolutionFullscreen((v) => !v)}
                        title="Fullscreen"
                        aria-label="Fullscreen"
                      >
                        <FiMaximize2 size={14} />
                      </button>
                    </div>
                    <textarea
                      className="cp-code"
                      value={solutionStarters[solutionTemplateLang]}
                      onChange={(e) => {
                        setSolutionStartersTouched(true);
                        setSolutionStarters((prev) => ({
                          ...prev,
                          [solutionTemplateLang]: e.target.value,
                        }));
                      }}
                      spellCheck={false}
                      rows={solutionFullscreen ? 24 : 10}
                    />
                  </div>
                </div>
              </section>
            )}

            {/* Tests */}
            <section className="cp-card">
              <div className="cp-card-head">
                <h2>{t('suggestProblem.sectionTests')}</h2>
                <span
                  className="cp-hint"
                  tabIndex={0}
                  aria-label={
                    isCreateMode ? t('createProblem.testsHint') : t('suggestProblem.testsHint')
                  }
                >
                  <FiAlertCircle size={15} aria-hidden />
                  <span className="cp-hint-tooltip" role="tooltip">
                    {isCreateMode ? t('createProblem.testsHint') : t('suggestProblem.testsHint')}
                  </span>
                </span>
              </div>
              <div className="cp-card-body">
                {isCreateMode && (
                  <div className="cp-format-guide" role="note">
                    <p className="cp-format-guide__title">{t('createProblem.testsGuideTitle')}</p>
                    <p className="cp-format-guide__text">{t('createProblem.testsGuideBody')}</p>
                    <pre className="cp-format-guide__example">{createTestInputPlaceholder}
→ 3</pre>
                    <p className="cp-format-guide__alt">{t('createProblem.testsGuideAlt')}</p>
                  </div>
                )}
                <div className="cp-side-actions">
                  <button type="button" className="cp-btn cp-btn--sm cp-btn--ghost" onClick={addTest}>
                    <FiPlus size={14} />
                    {t('suggestProblem.addTest')}
                  </button>
                  {!isCreateMode && (
                    <button type="button" className="cp-btn cp-btn--sm cp-btn--ghost" onClick={importTests}>
                      <FiUpload size={14} />
                      {t('suggestProblem.importTests')}
                    </button>
                  )}
                </div>

                <div className="cp-tests-table-wrap">
                  <table className="cp-tests-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('suggestProblem.exampleInput')}</th>
                        <th>{t('suggestProblem.expectedOutput')}</th>
                        <th>{t('suggestProblem.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tests.map((tc, idx) => (
                        <React.Fragment key={tc.id}>
                          <tr className={editingTestId === tc.id ? 'is-editing' : ''}>
                            <td>{idx + 1}</td>
                            <td className="cp-mono">{tc.input.trim() ? tc.input : '—'}</td>
                            <td className="cp-mono">{tc.output.trim() ? tc.output : '—'}</td>
                            <td>
                              <div className="cp-row-actions">
                                <button
                                  type="button"
                                  className="cp-icon-btn"
                                  onClick={() => setEditingTestId(editingTestId === tc.id ? null : tc.id)}
                                  aria-label="Edit"
                                >
                                  <FiEdit2 size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="cp-icon-btn"
                                  onClick={() => removeTest(tc.id)}
                                  aria-label="Delete"
                                >
                                  <FiTrash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {editingTestId === tc.id && (
                            <tr className="cp-test-edit-row">
                              <td colSpan={4}>
                                <div className="cp-test-edit">
                                  <div className="cp-field">
                                    <label>{t('suggestProblem.exampleInput')}</label>
                                    <textarea
                                      value={tc.input}
                                      onChange={(e) => updateTest(tc.id, { input: e.target.value })}
                                      rows={3}
                                      placeholder={
                                        isCreateMode
                                          ? createTestInputPlaceholder
                                          : t('suggestProblem.placeholderExampleInput')
                                      }
                                    />
                                  </div>
                                  <div className="cp-field">
                                    <label>{t('suggestProblem.expectedOutput')}</label>
                                    <input
                                      type="text"
                                      value={tc.output}
                                      onChange={(e) => updateTest(tc.id, { output: e.target.value })}
                                      placeholder={isCreateMode ? '3' : undefined}
                                    />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="cp-tests-footer">
                  <p>
                    {t('suggestProblem.testsSummary').replace('{total}', String(tests.length))}
                  </p>
                </div>
              </div>
            </section>

            {/* Stdin template — only for suggest flow */}
            {!isCreateMode && (
              <section className={`cp-card ${templateFullscreen ? 'is-fullscreen' : ''}`}>
                <div className="cp-card-head">
                  <h2>{t('suggestProblem.sectionTemplate')}</h2>
                  <button type="button" className="cp-btn cp-btn--sm cp-btn--ghost" onClick={resetStarter}>
                    <FiRefreshCw size={13} />
                    {t('suggestProblem.resetTemplate')}
                  </button>
                </div>
                <div className="cp-card-body">
                  <div className="cp-lang-tabs">
                    {(
                      [
                        ['python', 'Python 3'],
                        ['cpp', 'C++17'],
                        ['java', 'Java 17'],
                      ] as const
                    )
                      .filter(([key]) => languages.includes(key))
                      .map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          className={templateLang === key ? 'is-active' : ''}
                          onClick={() => setTemplateLang(key)}
                        >
                          {label}
                        </button>
                      ))}
                  </div>
                  <div className="cp-code-wrap">
                    <div className="cp-code-tools">
                      <button type="button" className="cp-icon-btn" onClick={copyStarter} title="Copy" aria-label="Copy">
                        <FiCopy size={14} />
                      </button>
                      <button
                        type="button"
                        className="cp-icon-btn"
                        onClick={() => setTemplateFullscreen((v) => !v)}
                        title="Fullscreen"
                        aria-label="Fullscreen"
                      >
                        <FiMaximize2 size={14} />
                      </button>
                    </div>
                    <textarea
                      className="cp-code"
                      value={starters[templateLang]}
                      onChange={(e) =>
                        setStarters((prev) => ({
                          ...prev,
                          [templateLang]: e.target.value,
                        }))
                      }
                      spellCheck={false}
                      rows={templateFullscreen ? 24 : 12}
                    />
                  </div>
                </div>
              </section>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
